import { useState, useEffect } from "react";
import { useParams, Link } from "react-router";
import { ExternalLink, Loader2, AlertCircle, ArrowLeft, Globe, Github, Twitter } from "lucide-react";
import { lookupEns, type EnsResult } from "@/lib/api";
import { IMG_BASE, POPULAR_ENS } from "@/lib/constants";
import { PageLayout } from "@/components/layout/page-layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CodeBlock } from "@/components/ui/code-block";

const AVATAR_FALLBACK_QUERY = "&fallback=boring";
const AVATAR_VARIANT_EXAMPLE = "boring-marble";

interface EnsPageProps {
  serverData?: EnsResult | null;
}

export default function EnsPage({ serverData }: EnsPageProps) {
  const { name } = useParams<{ name: string }>();
  const [ens, setEns] = useState<EnsResult | null>(serverData ?? null);
  const [loading, setLoading] = useState(!serverData);

  useEffect(() => {
    if (serverData) return;
    if (!name) return;
    setLoading(true);
    setEns(null);
    lookupEns(name)
      .then(setEns)
      .catch(() => setEns({ error: "Failed to load ENS data" } as EnsResult))
      .finally(() => setLoading(false));
  }, [name, serverData]);

  if (loading) {
    return (
      <div className="py-32 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-4" />
        <p className="text-sm text-muted-foreground">Resolving ENS name...</p>
      </div>
    );
  }

  if (!ens || ens.error) {
    return (
      <div className="py-32 text-center">
        <div className="max-w-md mx-auto px-6">
          <AlertCircle className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
          <h1 className="text-2xl font-extrabold tracking-tight mb-2">ENS name not found</h1>
          <p className="text-sm text-secondary-foreground mb-6">
            {ens?.error || `We couldn't resolve "${name}".`}
          </p>
          <Button asChild>
            <Link to="/"><ArrowLeft className="w-4 h-4" />Back to Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <PageLayout breadcrumbs={[{ label: "ENS", to: "/" }, { label: name! }]}>
      {/* Hero */}
      <section className="mb-12">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="shrink-0">
            <img
              src={`${IMG_BASE}/ens/${name}?size=256${AVATAR_FALLBACK_QUERY}`}
              alt={`${name} ENS avatar`}
              className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl object-contain bg-white border shadow-sm"
              onError={(e) => { (e.target as HTMLElement).style.opacity = "0.3"; }}
            />
          </div>
          <div className="min-w-0">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">{name}</h1>
            {ens.description && (
              <p className="text-base text-secondary-foreground leading-relaxed mb-3 max-w-2xl">{ens.description}</p>
            )}
            <div className="flex gap-2 flex-wrap">
              <Badge variant="secondary">ENS Name</Badge>
              {ens.address && (
                <Badge variant="outline" className="font-mono text-xs">
                  {ens.address.slice(0, 6)}...{ens.address.slice(-4)}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Usage */}
      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">Quick Usage</h2>
        <CodeBlock language="markup" code={`<img src="${IMG_BASE}/ens/${name}" />\n<img src="${IMG_BASE}/ens/${name}?fallback=${AVATAR_VARIANT_EXAMPLE}" />`} />
      </section>

      {/* Social Links */}
      {Object.values(ens.socials).some(Boolean) && (
        <section className="mb-12">
          <h2 className="text-lg font-bold tracking-tight mb-3">Connected Accounts</h2>
          <div className="flex gap-2 flex-wrap">
            {ens.socials.github && (
              <a href={`https://github.com/${ens.socials.github}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-card border rounded-xl text-sm font-medium text-secondary-foreground hover:text-foreground hover:border-border-strong hover:shadow-sm transition-all no-underline">
                <Github className="w-4 h-4" /> {ens.socials.github} <ExternalLink className="w-3 h-3 text-muted-foreground" />
              </a>
            )}
            {ens.socials.twitter && (
              <a href={`https://twitter.com/${ens.socials.twitter}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-card border rounded-xl text-sm font-medium text-secondary-foreground hover:text-foreground hover:border-border-strong hover:shadow-sm transition-all no-underline">
                <Twitter className="w-4 h-4" /> @{ens.socials.twitter} <ExternalLink className="w-3 h-3 text-muted-foreground" />
              </a>
            )}
            {ens.socials.url && (
              <a href={ens.socials.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-card border rounded-xl text-sm font-medium text-secondary-foreground hover:text-foreground hover:border-border-strong hover:shadow-sm transition-all no-underline">
                <Globe className="w-4 h-4" /> Website <ExternalLink className="w-3 h-3 text-muted-foreground" />
              </a>
            )}
          </div>
        </section>
      )}

      {/* API Usage */}
      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">API Usage</h2>
        <CodeBlock language="markup" code={`<!-- ENS avatar image -->\n<img src="${IMG_BASE}/ens/${name}" />\n\n<!-- Variant-specific avatar fallback -->\n<img src="${IMG_BASE}/ens/${name}?size=256&fallback=${AVATAR_VARIANT_EXAMPLE}" />\n\n<!-- Supported fallback variants -->\n<!-- boring, boring-beam, boring-marble, boring-pixel, boring-sunset, boring-ring, boring-bauhaus, boring-geometric, boring-abstract -->\n\n<!-- ENS profile data (JSON) -->\n<script>\nfetch("https://api.loadlogo.com/ens/${name}")\n</script>`} />
      </section>

      {/* Related ENS Names */}
      <section>
        <h2 className="text-lg font-bold tracking-tight mb-4">Popular ENS Names</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {POPULAR_ENS.filter((e) => e !== name).slice(0, 8).map((ensName) => (
            <Link key={ensName} to={`/ens/${ensName}`} className="flex items-center gap-3 p-3 bg-card border rounded-xl hover:shadow-md hover:-translate-y-0.5 transition-all no-underline">
              <img src={`${IMG_BASE}/ens/${ensName}?size=64${AVATAR_FALLBACK_QUERY}`} alt={ensName} className="w-8 h-8 rounded-lg object-contain" loading="lazy" />
              <span className="text-sm font-medium text-secondary-foreground truncate">{ensName}</span>
            </Link>
          ))}
        </div>
      </section>
    </PageLayout>
  );
}
